/* @flow */
import _ from 'lodash';

import type {
  ReduxState,
  Action,
  LiferayError,
  SearchParams,
  TrackSearchFullParams,
  MediaSearchFullParams,
} from '../../types';
import { searchParamsProps } from '../../types/SearchParams';
import stringUtils from '../../utils/string';

// ===========================
//   STATE TYPE
// ===========================
export type AppState = {
  isInitialized: boolean,
  isInitializing: boolean,
  initializationError: ?LiferayError,
  tracksDownloadUrl?: string,
  isSnackbarVisible: boolean,
  snackbarMessage: string,
  snackbarIsError: boolean,
  snackbarIsSticky: boolean,
  searchParams: SearchParams,
  submittedSearchParams: SearchParams,
  searchHistory: SearchParams[],
};

// ===========================
//   INITIAL STATE
// ===========================
export const initialState: AppState = {
  isInitialized: false,
  isInitializing: false,
  initializationError: null,
  isSnackbarVisible: false,
  snackbarMessage: '',
  snackbarIsError: false,
  snackbarIsSticky: false,
  searchParams: {
    text: '',
    flags: {
      title: false,
      people: false,
      tags: false,
    },
    filters: {},
  },
  submittedSearchParams: {
    text: '',
    flags: {
      title: false,
      people: false,
      tags: false,
    },
    filters: {},
  },
  searchHistory: [],
};

// ===========================
//   REDUCER
// ===========================
export default (state: AppState = initialState, action: Action): AppState => {
  switch (action.type) {
    case 'INITIALIZE_APP_REQUEST':
      return { ...state, isInitializing: true };
    case 'INITIALIZE_APP_SUCCESS':
      return { ...state, isInitialized: true, isInitializing: false };
    case 'INITIALIZE_APP_FAILURE':
      return {
        ...state,
        isInitializing: false,
        initializationError: action.payload,
      };
    // case 'FETCH_TRACKS_DOWNLOAD_URL_REQUEST':
    //   return { ...state, isInitializing: true };
    case 'FETCH_TRACKS_DOWNLOAD_URL_SUCCESS':
      return { ...state, tracksDownloadUrl: action.payload };
    case 'RESET_TRACKS_DOWNLOAD_URL':
      return { ...state, tracksDownloadUrl: undefined };
    case 'SET_SEARCH':
      return { ...state, searchParams: action.payload };
    case 'SET_TRACK_SEARCH': {
      const fullSearchParams: TrackSearchFullParams = action.payload;
      const searchParams: SearchParams = _.pick(fullSearchParams, searchParamsProps);
      return { ...state, searchParams };
    }
    case 'SET_MEDIA_SEARCH': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: SearchParams = _.pick(fullSearchParams, searchParamsProps);
      return { ...state, searchParams };
    }
    case 'PERFORM_TRACK_SEARCH_REQUEST': {
      const fullSearchParams: TrackSearchFullParams = action.payload;
      const searchParams: SearchParams = _.pick(fullSearchParams, searchParamsProps);
      const searchHistory = _.take([searchParams, ...state.searchHistory], 100);
      return {
        ...state,
        searchParams,
        submittedSearchParams: searchParams,
        searchHistory: stringUtils.caseInsensitiveUniqBy(searchHistory, params => params.text),
      };
    }
    case 'PERFORM_MEDIA_SEARCH_REQUEST': {
      const fullSearchParams: MediaSearchFullParams = action.payload;
      const searchParams: SearchParams = _.pick(fullSearchParams, searchParamsProps);
      const searchHistory = _.take([searchParams, ...state.searchHistory], 100);
      return {
        ...state,
        searchParams,
        submittedSearchParams: searchParams,
        searchHistory: stringUtils.caseInsensitiveUniqBy(searchHistory, params => params.text),
      };
    }
    case 'HIDE_SNACKBAR':
      return { ...state, isSnackbarVisible: false };
    case 'NOT_IMPLEMENTED':
      return {
        ...state,
        isSnackbarVisible: true,
        snackbarMessage: 'Funzione non ancora disponibile',
      };
    case 'DISPLAY_MESSAGE':
      const snackbarMessage = action.payload.message;
      const snackbarIsError = action.payload.isError;
      const snackbarIsSticky = !!action.payload.isSticky;
      return {
        ...state,
        isSnackbarVisible: true,
        snackbarMessage,
        snackbarIsError,
        snackbarIsSticky,
      };
    case 'LOGOUT_SUCCESS':
      return {
        ...initialState,
        isInitialized: state.isInitialized,
        initializationError: state.initializationError,
      };
    default:
  }

  if (action.type.endsWith('_FAILURE')) {
    const message = _.get(action, 'payload.message', 'Errore sconosciuto');
    return {
      ...state,
      isSnackbarVisible: true,
      snackbarMessage: message,
      snackbarIsError: true,
    };
  }

  if (action.type.endsWith('_SUCCESS') && action.meta && action.meta.successMessage) {
    const message: any = action.meta.successMessage;
    return {
      ...state,
      isSnackbarVisible: true,
      snackbarMessage: message,
      snackbarIsError: false,
    };
  }

  return state;
};

// ===========================
//   SELECTORS
// ===========================

export const getSearchParams = (state: ReduxState): SearchParams => {
  return state.app.searchParams;
};

export const getSubmittedSearchParams = (state: ReduxState): SearchParams => {
  return state.app.submittedSearchParams;
};

export const getTracksDownloadUrl = (state: ReduxState): string | void => {
  return state.app.tracksDownloadUrl;
};
